/**
 * CONNECTION RUNBOOK do OrdersRouter (bloco session-transport 🔴) — extraído
 * de ordersRouter.jsx na modularização dos god-objects: registro/limpeza de
 * event handlers SignalR, login (auth HTTP com failover multi-servidor),
 * stop, clearRouterData (+ callback single-slot onRouterDataCleared do corte
 * de ciclo com o orderStore), política de reconexão (backoff exponencial com
 * cap, decisão pós-onclose, classificação recoverable × auth-fatal),
 * checkConnectivity, refreshToken/getValidToken (estado próprio deste
 * serviço) e runConnection, além de receiveInfo/receiveHeartbeat/
 * isConnectionOK/inTradeState.
 *
 * State machine de sessão: corpos preservados byte-a-byte, `this` → `rt`;
 * estado NA INSTÂNCIA; chamadas internas via `rt.<método>` (delegates).
 * MOVER, nunca reestruturar aqui.
 *
 * Contratos: routerClearSeam.test.js (stop→clear→callback),
 * ordersRouterSessionTransportP1.test.js (decisão de reconexão),
 * routerOrderCommandsContract.test.js (guards de estado) + execução REAL —
 * todo login de e2e com router roda este runbook ponta-a-ponta.
 */
import * as signalR from "@microsoft/signalr";
import marketdata from "./marketdata.jsx";
import * as utilities from "./utilites.jsx";
import { parsedBrokerUrls } from "./settings.jsx";
import logger from "../../utils/logger.js";
import { globalToast } from "../../components/ToastProvider.jsx";
import timeoutManager from "../../utils/timeoutManager.js";
import { urlManager } from "../../utils/urlManager.js";
import { PRODUCT_NAME, PRODUCT_VERSION } from "../../constants/product.js";
import {
  isTradingDisabled,
  getTradingDisabledReason,
} from "../../utils/mobileDetection.js";

export const unregisterAllEventHandlers = (rt) => {
  if (!rt.signalRConnection || typeof rt.signalRConnection.off !== "function") {
    console.log(
      `[${new Date().toISOString()}] ℹ️  No SignalR connection to clean up`,
    );
    return;
  }

  try {
    // Remove all known event handlers
    rt.signalRConnection.off("accountData");
    rt.signalRConnection.off("rpt");
    rt.signalRConnection.off("info");
    rt.signalRConnection.off("hb");
    rt.signalRConnection.off("disconnected");

    console.log(
      `[${new Date().toISOString()}] 🧹 SignalR event handlers unregistered`,
    );
  } catch (error) {
    console.warn(
      `[${new Date().toISOString()}] ⚠️  Error during event handler cleanup:`,
      error,
    );
  }
};

// Simplified event handler registration
export const registerEventHandlers = (rt) => {
  if (!rt.signalRConnection || typeof rt.signalRConnection.on !== "function") {
    console.warn(
      `[${new Date().toISOString()}] ⚠️  Cannot register event handlers: Invalid connection`,
    );
    return;
  }

  try {
    rt.signalRConnection.on("accountData", rt.receiveAccountData.bind(this));
    rt.signalRConnection.on("rpt", rt.receiveReport.bind(this));
    rt.signalRConnection.on("info", rt.receiveInfo.bind(this));
    rt.signalRConnection.on("hb", rt.receiveHeartbeat.bind(this));
    rt.signalRConnection.on("disconnected", (from, message) => {
      console.log(
        `[${new Date().toISOString()}] 📡 Server disconnected router`,
      );
      rt.state = "offline";
      rt.closedByServer = true;

      // Clear all router-related data when disconnected
      rt.clearRouterData();

      timeoutManager.setTimeout(
        () => {
          if (message === undefined) {
            message =
              "Você foi desconectado do servidor de roteador de ordens.";
          }
          utilities.errorHandler(message);
        },
        0,
        "order-router-error-display",
      );
    });

    console.log(
      `[${new Date().toISOString()}] 📡 SignalR event handlers registered`,
    );
  } catch (error) {
    console.error(
      `[${new Date().toISOString()}] ❌ Error registering event handlers:`,
      error,
    );
  }
};

export const login = async (rt, broker, user, pwd) => {
  // Safety check: Block router login on mobile phones
  if (isTradingDisabled()) {
    const reason = getTradingDisabledReason();
    if (reason === "mobile") {
      const message =
        "⚠️ Negociação não disponível em celulares. Use um tablet ou computador para acessar o roteador.";
      globalToast.showInfo(message, "Negociação Indisponível");
      rt.idle = true;
      return message;
    }
    // Allow login during replay mode (just blocks actual orders)
  }

  rt.broker = broker; // When rico or ricon, it will always be RICO.
  rt.user = user;
  rt.pwd = pwd;
  rt.idle = false;
  rt.manualLogout = false; // Reset manual logout flag on new login attempt

  const urls = parsedBrokerUrls[rt.broker] || [];
  let lastError = null;
  const maxServers = urls.length || 1;

  // Try all servers sequentially until one succeeds
  for (let attempt = 0; attempt < maxServers; attempt++) {
    try {
      const tokenUrl = rt.getUrl() + "/api/security/auth";
      const parameters = {
        Broker: rt.broker,
        BrokerClientId: rt.user,
        BrokerClientPwd: rt.pwd,
        Product: PRODUCT_NAME,
        Version: PRODUCT_VERSION,
      };

      console.log(
        `[${new Date().toISOString()}] 🔐 Attempting router login on server ${urlManager.getCurrentIndex(broker, "broker")}: ${rt.getUrl()} (${PRODUCT_NAME} v${PRODUCT_VERSION})`,
      );

      const resp = await utilities.postJsonToURL(
        tokenUrl,
        parameters,
        undefined,
      );
      const r = JSON.parse(resp);

      if (r.logged == true) {
        const ntk = r.info;
        rt.signalRToken = ntk;

        // Mark success
        if (urls.length > 0) {
          urlManager.markSuccess(rt.broker, "broker");
        }

        return await rt.runConnection();
      } else {
        rt.idle = true;
        const message = decodeURIComponent(r.rejectedMessage);

        logger.orders.error(`Erro no login: ${message}`, {
          type: "erro_login",
          message: message,
          broker: rt.broker,
          user: rt.user,
        });

        // Check for specific error conditions that require automatic action
        if (message.includes("Senha Expirada")) {
          console.log(
            `[${new Date().toISOString()}] 🔐 Senha expirada detectada - disparando evento para abrir modal de alteração`,
          );

          // Emit custom event for password expiration
          window.dispatchEvent(
            new CustomEvent("routerPasswordExpired", {
              detail: {
                broker: rt.broker,
                user: rt.user,
                message: message,
              },
            }),
          );

          // Show specific error message
          globalToast.showError(
            "Sua senha de roteamento expirou. Por favor, altere sua senha para continuar.",
            "Senha Expirada",
          );
        } else if (message.includes("Acesso bloqueado")) {
          console.log(
            `[${new Date().toISOString()}] 🚫 Acesso bloqueado detectado - disparando evento para abrir modal de recuperação`,
          );

          // Emit custom event for blocked access
          window.dispatchEvent(
            new CustomEvent("routerAccessBlocked", {
              detail: {
                broker: rt.broker,
                user: rt.user,
                message: message,
              },
            }),
          );

          // Show specific error message with guidance
          globalToast.showError(
            'Acesso bloqueado. Use o diálogo "Esqueci minha senha" para desbloquear seu acesso.',
            "Acesso Bloqueado",
          );
        }
        // Don't show toast for generic errors - let RouterLoginModal handle it

        return r.rejectedMessage;
      }
    } catch (e) {
      lastError = e;
      console.error(
        `[${new Date().toISOString()}] ❌ Router login failed on ${rt.getUrl()}:`,
        e.message || e,
      );

      // If multiple servers configured, try next one
      if (urls.length > 1 && attempt < maxServers - 1) {
        const nextUrl = urlManager.markFailureAndGetNext(
          rt.broker,
          "broker",
          urls,
          e,
        );
        if (nextUrl) {
          console.log(
            `[${new Date().toISOString()}] 🔄 Trying next router server: ${nextUrl}`,
          );
          // Small delay to avoid hammering servers
          await new Promise((resolve) => setTimeout(resolve, 500));
          continue; // Try next server
        }
      }

      // Last server or single server - break and show error
      break;
    }
  }

  // All servers failed - show error
  if (lastError) {
    if (lastError && lastError.message) {
      rt.idle = true;
      utilities.errorHandler(lastError);
    } else {
      // Check if this is a recoverable error for initial connection
      const isRecoverableError = rt.isRecoverableError(lastError);
      console.log(
        `[${new Date().toISOString()}] 🔍 Initial connection error recoverable: ${isRecoverableError}`,
      );

      if (
        isRecoverableError &&
        rt.reconnectionAttempts < rt.maxReconnectionAttempts &&
        !rt.manualLogout
      ) {
        const delay = rt.getReconnectionDelay();
        console.log(
          `[${new Date().toISOString()}] 🔄 Initial connection failed, scheduling reconnection attempt ${rt.reconnectionAttempts + 1}/${rt.maxReconnectionAttempts} in ${delay / 1000} seconds`,
        );

        timeoutManager.setTimeout(async () => {
          const attemptNumber = rt.reconnectionAttempts + 1;
          const attemptTimestamp = new Date().toISOString();

          console.log(
            `[${attemptTimestamp}] 🔄 Starting initial connection attempt ${attemptNumber}/${rt.maxReconnectionAttempts}`,
          );
          console.log(
            `[${attemptTimestamp}] ⏱️  Delay since last attempt: ${delay / 1000} seconds`,
          );
          console.log(`[${attemptTimestamp}] 📊 Current reconnection stats:`, {
            attempt: attemptNumber,
            maxAttempts: rt.maxReconnectionAttempts,
            delayMs: delay,
            broker: rt.broker,
            serverUrl: rt.getUrl(),
            triggerType: "initial_connection_failure",
            errorType: lastError?.message,
          });

          const connectivityCheckStart = Date.now();
          const isConnected = await rt.checkConnectivity();
          const connectivityCheckDuration = Date.now() - connectivityCheckStart;

          if (isConnected) {
            console.log(
              `[${new Date().toISOString()}] ✅ Network connectivity confirmed (${connectivityCheckDuration}ms)`,
            );
            console.log(
              `[${new Date().toISOString()}] 🚀 Initiating initial connection attempt to ${rt.getUrl()}`,
            );

            // Increment attempts before actual connection attempt
            rt.reconnectionAttempts++;

            try {
              await rt.login(rt.broker, rt.user, rt.pwd);
              console.log(
                `[${new Date().toISOString()}] 🎉 Initial connection attempt ${attemptNumber} successful!`,
              );
            } catch (reconnectError) {
              console.error(
                `[${new Date().toISOString()}] ❌ Initial connection attempt ${attemptNumber} failed:`,
                reconnectError,
              );
              // Continue with next attempt if we haven't reached max attempts
              if (rt.reconnectionAttempts < rt.maxReconnectionAttempts) {
                console.log(
                  `[${new Date().toISOString()}] ⏭️  Preparing next initial connection attempt...`,
                );
              }
            }
          } else {
            console.warn(
              `[${new Date().toISOString()}] 🚫 Network connectivity check failed (${connectivityCheckDuration}ms)`,
            );
            console.log(
              `[${new Date().toISOString()}] ⏳ Skipping initial connection attempt ${attemptNumber}, will retry in ${delay / 1000} seconds`,
            );
            // Don't increment attempts for connectivity issues
            // Try again after the same delay
            timeoutManager.setTimeout(() => {
              console.log(
                `[${new Date().toISOString()}] 🔄 Retrying initial connection after connectivity check failure...`,
              );
              rt.login(rt.broker, rt.user, rt.pwd);
            }, delay);
          }
        }, delay);
      } else if (!isRecoverableError) {
        console.log(
          `[${new Date().toISOString()}] 🚫 Initial connection error is not recoverable, showing error to user`,
        );
        utilities.errorHandler(lastError);
      } else {
        console.error(
          `[${new Date().toISOString()}] Maximum reconnection attempts (${rt.maxReconnectionAttempts}) reached for initial connection. Giving up.`,
        );
        utilities.errorHandler(
          "Falha ao conectar após várias tentativas. Verifique sua conexão de internet.",
        );
      }
    }
  }
};

export const stop = (rt) => {
  console.log(
    `[${new Date().toISOString()}] 🛑 Stopping order router connection (manual logout)`,
  );

  // Set manual logout flag to prevent reconnection attempts
  rt.manualLogout = true;

  // Clean up SignalR connection
  if (rt.signalRConnection && typeof rt.signalRConnection.stop === "function") {
    rt.unregisterAllEventHandlers();
    rt.signalRConnection.stop().catch((error) => {
      console.warn(
        `[${new Date().toISOString()}] ⚠️  Error stopping SignalR connection:`,
        error,
      );
    });
  }

  // Clear all router-related data
  rt.clearRouterData();

  // Reset state
  rt.state = "offline";
  rt.idle = true;
  rt.closedByServer = false;
  rt.resetReconnectionAttempts();

  // Clean up intervals and timeouts
  if (rt.refreshTokenIntervalId != 0) {
    clearInterval(rt.refreshTokenIntervalId);
    rt.refreshTokenIntervalId = 0;
  }
  timeoutManager.clearAllTimeouts("order-router-stop");

  console.log(
    `[${new Date().toISOString()}] ✅ Order router stopped (manual logout)`,
  );
};

// Register the cleanup to run whenever router data is cleared (disconnect /
// manual logout). Single slot — last registration wins.
export const onRouterDataCleared = (rt, fn) => {
  if (
    rt._onRouterDataCleared &&
    fn &&
    fn !== rt._onRouterDataCleared &&
    import.meta.env?.MODE !== "production"
  ) {
    console.warn(
      "⚠️ Router.onRouterDataCleared: overwriting an existing handler — the previous cleanup will no longer run",
    );
  }
  rt._onRouterDataCleared = fn;
};

// Clear all router-related data from stores
export const clearRouterData = (rt) => {
  console.log(
    `[${new Date().toISOString()}] 🧹 Clearing all router-related data`,
  );

  // Unregister all position symbols from quote subscriptions
  if (rt._registeredPositionSymbols && rt._registeredPositionSymbols.size > 0) {
    for (const symbol of rt._registeredPositionSymbols) {
      marketdata.unregisterQuote(symbol, "positions");
    }
    rt._registeredPositionSymbols = new Set();
  }

  // Clear account data and deduplication cache
  rt.accountData = null;
  rt._lastAccountDataString = null;

  // Notify all receivers that account data has been cleared
  rt.receivers.forEach((receiver) => {
    if (receiver.receiveAccountData) {
      receiver.receiveAccountData(null);
    }
  });

  // Clear order store data (callback registered by orderStore at module load;
  // null only if the orderStore module was never imported — then there is no
  // store state to clear)
  if (rt._onRouterDataCleared) {
    rt._onRouterDataCleared();
  } else {
    console.warn(
      "⚠️ Router.clearRouterData: no onRouterDataCleared handler registered — order store not cleared (expected only if the orderStore module was never imported)",
    );
  }

  console.log(`[${new Date().toISOString()}] ✅ Router data cleared`);
};

// Advanced reconnection utility methods (matching marketdata.jsx patterns)
export const getReconnectionDelay = (rt) => {
  const delay = rt.baseReconnectionDelay * Math.pow(2, rt.reconnectionAttempts);
  return Math.min(delay, rt.maxReconnectionDelay);
};

export const resetReconnectionAttempts = (rt) => {
  rt.reconnectionAttempts = 0;
};

// Decisão pura de reconexão pós-onclose (bloco session-transport). Extraída do
// closure de runConnection p/ testabilidade: reconecta só em close limpo
// (error===undefined) ou erro recuperável, E nunca quando o servidor evictou
// (closedByServer) ou o usuário deslogou (manualLogout). Comportamento
// idêntico ao inline original.
// NOTA DE REGRESSÃO (#identity-guard): diferente do marketdata, esta decisão
// NÃO verifica se rt.signalRConnection foi substituída por uma conexão mais
// nova — um close de socket OBSOLETO ainda passa por aqui e pode rearmar a
// escada de reconexão. O teste documenta essa assimetria.
export const _shouldAttemptReconnectionOnClose = (rt, error) => {
  return (
    (error === undefined ||
      (error !== undefined && rt.isRecoverableError(error))) &&
    !rt.closedByServer &&
    !rt.manualLogout
  );
};

export const isRecoverableError = (rt, error) => {
  if (!error) return false;

  const errorMessage = error.message || error.toString();

  // WebSocket 1006 (no reason given) - usually recoverable
  if (errorMessage.includes("1006")) return true;

  // Network-related errors that are usually recoverable
  if (errorMessage.includes("NetworkError")) return true;
  if (errorMessage.includes("Failed to fetch")) return true;
  if (errorMessage.includes("connection")) return true;
  if (errorMessage.includes("timeout")) return true;

  // Authentication errors are NOT recoverable
  if (errorMessage.includes("401")) return false;
  if (errorMessage.includes("403")) return false;
  if (errorMessage.includes("Forbidden")) return false;
  if (errorMessage.includes("Unauthorized")) return false;

  // Default to recoverable for unknown errors
  return true;
};

export const checkConnectivity = async (rt) => {
  try {
    // Try to fetch a lightweight endpoint to test connectivity.
    // Use getValidToken (not the raw signalRToken) so an expired token is
    // refreshed first — mirrors the marketdata fix; avoids a stale-token 401
    // being misread as a connectivity failure during reconnection.
    const token = await rt.getValidToken();
    const testUrl = `${rt.getUrl()}/api/security/ping`;
    const response = await fetch(testUrl, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
      },
      signal: AbortSignal.timeout(5000), // 5 second timeout
    });
    return response.ok;
  } catch (error) {
    console.log("Order router connectivity check failed:", error.message);
    return false;
  }
};

export const refreshToken = async (rt) => {
  try {
    const tokenUrl = rt.getUrl() + "/api/security/renewtoken";
    rt.signalRToken = await utilities.postJsonToURL(
      tokenUrl,
      undefined,
      rt.signalRToken,
    );
    return rt.signalRToken;
  } catch (error) {
    utilities.errorHandler(error);
    throw error;
  }
};

export const getValidToken = async (rt) => {
  // If no token exists, we need a new one
  if (!rt.signalRToken) {
    console.log(
      `[${new Date().toISOString()}] 🔑 No token available, refreshing...`,
    );
    await rt.refreshToken();
    return rt.signalRToken;
  }

  // Check if current token is expired
  if (rt.isTokenExpired(rt.signalRToken)) {
    const expirationDate = rt.parseTokenExpiration(rt.signalRToken);
    console.log(
      `[${new Date().toISOString()}] 🔑 Token expired (${expirationDate?.toISOString()}), refreshing...`,
    );
    await rt.refreshToken();

    // Log new token expiration
    const newExpirationDate = rt.parseTokenExpiration(rt.signalRToken);
    console.log(
      `[${new Date().toISOString()}] ✅ Token refreshed, new expiration: ${newExpirationDate?.toISOString()}`,
    );

    return rt.signalRToken;
  }

  // Token is still valid
  return rt.signalRToken;
};

export const runConnection = async (rt) => {
  const connectionStartTime = new Date().toISOString();
  console.log(
    `[${connectionStartTime}] 🔧 Starting order router SignalR connection process`,
  );
  console.log(`[${connectionStartTime}] 📡 Target server: ${rt.getUrl()}`);
  console.log(`[${connectionStartTime}] 🏢 Broker: ${rt.broker}`);

  // Log para o usuário - apenas em reconexões
  if (rt.reconnectionAttempts > 0) {
    logger.orders.info("Conectando ao servidor de roteador de ordens", {
      server: rt.getUrl(),
    });
  }

  rt.unregisterAllEventHandlers();

  try {
    const options = {
      accessTokenFactory: () => rt.getValidToken(),
    };
    const rtserver = rt.getUrl() + "/rt?version=" + rt.getVersion();

    console.log(
      `[${new Date().toISOString()}] 🏗️  Building SignalR connection to: ${rtserver}`,
    );
    rt.signalRConnection = new signalR.HubConnectionBuilder()
      .withUrl(rtserver, options)
      .configureLogging(signalR.LogLevel.Error)
      .build();

    // Set up connection lifecycle handlers first
    rt.signalRConnection.onclose((error) => {
      const timestamp = new Date().toISOString();
      console.log(
        `[${timestamp}] Order router SignalR connection closed. State: ${rt.state}`,
      );

      rt.state = "offline";

      if (error !== undefined) {
        console.error(
          `[${timestamp}] Order router SignalR connection error:`,
          error,
        );

        logger.orders.error("Conexão perdida com roteador de ordens", {
          error: error.message,
          broker: rt.broker,
          server: rt.getUrl(),
          attempts: rt.reconnectionAttempts,
        });

        // Check if this is a recoverable error
        const isRecoverableError = rt.isRecoverableError(error);
        console.log(
          `[${timestamp}] 🔍 Error recoverable: ${isRecoverableError}`,
        );

        if (isRecoverableError) {
          console.log(
            `[${timestamp}] 🔄 Error is recoverable, attempting reconnection...`,
          );
        } else {
          console.log(
            `[${timestamp}] 🚫 Error is not recoverable, showing error to user`,
          );
          utilities.errorHandler(error);
        }
      } else {
        logger.orders.info("Conexão fechada normalmente com roteador", {
          broker: rt.broker,
          server: rt.getUrl(),
        });
      }

      // Attempt reconnection if conditions are met
      const shouldAttemptReconnection =
        rt._shouldAttemptReconnectionOnClose(error);

      if (
        shouldAttemptReconnection &&
        rt.reconnectionAttempts < rt.maxReconnectionAttempts
      ) {
        const delay = rt.getReconnectionDelay();
        console.log(
          `[${timestamp}] Reconnection attempt ${rt.reconnectionAttempts + 1}/${rt.maxReconnectionAttempts} scheduled in ${delay / 1000} seconds`,
        );

        timeoutManager.setTimeout(async () => {
          const attemptNumber = rt.reconnectionAttempts + 1;
          const attemptTimestamp = new Date().toISOString();

          console.log(
            `[${attemptTimestamp}] 🔄 Starting reconnection attempt ${attemptNumber}/${rt.maxReconnectionAttempts}`,
          );
          console.log(
            `[${attemptTimestamp}] ⏱️  Delay since last attempt: ${delay / 1000} seconds`,
          );
          console.log(`[${attemptTimestamp}] 📊 Current reconnection stats:`, {
            attempt: attemptNumber,
            maxAttempts: rt.maxReconnectionAttempts,
            delayMs: delay,
            broker: rt.broker,
            serverUrl: rt.getUrl(),
            triggerType: error ? "error_recovery" : "clean_disconnect",
            errorType: error ? error.message : null,
          });

          const connectivityCheckStart = Date.now();
          const isConnected = await rt.checkConnectivity();
          const connectivityCheckDuration = Date.now() - connectivityCheckStart;

          if (isConnected) {
            console.log(
              `[${new Date().toISOString()}] ✅ Network connectivity confirmed (${connectivityCheckDuration}ms)`,
            );
            console.log(
              `[${new Date().toISOString()}] 🚀 Initiating order router reconnection to ${rt.getUrl()}`,
            );

            // Increment attempts before actual reconnection
            rt.reconnectionAttempts++;

            try {
              await rt.login(rt.broker, rt.user, rt.pwd);
              console.log(
                `[${new Date().toISOString()}] 🎉 Reconnection attempt ${attemptNumber} successful!`,
              );

              // Log de sucesso de reconexão para o usuário
              logger.orders.success(
                "Reconexão bem-sucedida com roteador de ordens",
                {
                  attempt: attemptNumber,
                  server: rt.getUrl(),
                },
              );
            } catch (reconnectError) {
              console.error(
                `[${new Date().toISOString()}] ❌ Reconnection attempt ${attemptNumber} failed:`,
                reconnectError,
              );
              // Continue with next attempt if we haven't reached max attempts
              if (rt.reconnectionAttempts < rt.maxReconnectionAttempts) {
                console.log(
                  `[${new Date().toISOString()}] ⏭️  Preparing next reconnection attempt...`,
                );
              }
            }
          } else {
            console.warn(
              `[${new Date().toISOString()}] 🚫 Network connectivity check failed (${connectivityCheckDuration}ms)`,
            );
            console.log(
              `[${new Date().toISOString()}] ⏳ Skipping reconnection attempt ${attemptNumber}, will retry in ${delay / 1000} seconds`,
            );
            // Don't increment attempts for connectivity issues
            // Try again after the same delay
            timeoutManager.setTimeout(() => {
              console.log(
                `[${new Date().toISOString()}] 🔄 Retrying reconnection after connectivity check failure...`,
              );
              rt.login(rt.broker, rt.user, rt.pwd);
            }, delay);
          }
        }, delay);
      } else {
        // Log why reconnection was not attempted
        if (rt.manualLogout) {
          console.log(
            `[${timestamp}] 🚫 Reconnection skipped: Manual logout detected`,
          );
        } else if (rt.reconnectionAttempts >= rt.maxReconnectionAttempts) {
          console.error(
            `[${timestamp}] Maximum reconnection attempts (${rt.maxReconnectionAttempts}) reached. Giving up.`,
          );
          // Only show error to user if it's a non-recoverable error or no error occurred
          if (
            error === undefined ||
            (error !== undefined && !rt.isRecoverableError(error))
          ) {
            utilities.errorHandler(
              error ||
                "Falha ao reconectar após várias tentativas. Verifique sua conexão de internet.",
            );
          } else {
            utilities.errorHandler(
              "Falha ao reconectar após várias tentativas. Verifique sua conexão de internet.",
            );
          }
        } else {
          console.log(
            `[${timestamp}] 🚫 Reconnection skipped: Conditions not met (closedByServer: ${rt.closedByServer})`,
          );
        }
      }

      // Always notify receivers when connection is lost, regardless of the reason
      if (rt.receivers.length > 0) {
        timeoutManager.setTimeout(() => {
          const r = rt.receivers.slice(0);
          r.forEach((receiver) => {
            if (receiver.OnDisconnected !== undefined)
              receiver.OnDisconnected();
          });
        }, 0);
      }

      rt.closedByServer = false;
    });

    // Register event handlers before starting connection
    rt.registerEventHandlers();

    console.log(
      `[${new Date().toISOString()}] 🚀 Starting SignalR connection...`,
    );
    const connectionAttemptStart = Date.now();
    await rt.signalRConnection.start();
    const connectionDuration = Date.now() - connectionAttemptStart;
    console.log(
      `[${new Date().toISOString()}] ✅ SignalR connection established in ${connectionDuration}ms`,
    );

    // Mark server as healthy in URL manager
    const urls = parsedBrokerUrls[marketdata.broker] || [];
    if (urls.length > 0) {
      urlManager.markSuccess(marketdata.broker, "broker");
    }

    // Log de sucesso para o usuário
    logger.orders.success(
      "Conexão estabelecida com servidor de roteador de ordens",
      {
        duration: connectionDuration,
        server: rt.getUrl(),
      },
    );

    if (rt.refreshTokenIntervalId != 0) {
      clearInterval(rt.refreshTokenIntervalId);
      rt.refreshTokenIntervalId = 0;
    }

    console.log(
      `[${new Date().toISOString()}] ⏰ Setting up smart token refresh interval (5 minutes)`,
    );
    rt.refreshTokenIntervalId = setInterval(
      async () => {
        try {
          // Only refresh if token is expired or about to expire
          if (rt.isTokenExpired(rt.signalRToken, 10)) {
            // 10 minute buffer for periodic check
            console.log(
              `[${new Date().toISOString()}] 🔄 Periodic token refresh triggered`,
            );
            await rt.refreshToken();
          } else {
            const expirationDate = rt.parseTokenExpiration(rt.signalRToken);
            console.log(
              `[${new Date().toISOString()}] ✅ Token still valid, expires: ${expirationDate?.toISOString()}`,
            );
          }
        } catch (error) {
          console.error(
            `[${new Date().toISOString()}] ❌ Periodic token refresh failed:`,
            error,
          );
        }
      },
      5 * 60 * 1000,
    ); // Check every 5 minutes instead of 10

    // Always set state online when connected (regardless of receivers)
    // This ensures receiveAccountData processes data for performance testing
    rt.state = "online";
    // Reset reconnection attempts on successful connection
    rt.resetReconnectionAttempts();

    // Only notify receivers if they exist
    if (rt.receivers.length > 0) {
      timeoutManager.setTimeout(
        () => {
          const r = rt.receivers.slice(0);
          for (let i = 0; i < r.length; i++) {
            if (r[i].OnConnected !== undefined) r[i].OnConnected();
          }
        },
        0,
        "order-router-connected-notification",
      );
    }

    console.log(
      "📞 Router: Calling getAccountDataAsync after successful connection",
    );
    rt.getAccountDataAsync();

    const totalConnectionTime = Date.now() - Date.parse(connectionStartTime);
    console.log(
      `[${new Date().toISOString()}] 🎉 Order router connection process completed successfully in ${totalConnectionTime}ms`,
    );
    console.log(`[${new Date().toISOString()}] 📈 Connection stats:`, {
      totalTime: totalConnectionTime,
      connectionTime: connectionDuration,
      broker: rt.broker,
      serverUrl: rt.getUrl(),
    });
  } catch (e) {
    const failureTime = new Date().toISOString();
    const totalAttemptTime = Date.now() - Date.parse(connectionStartTime);

    console.error(
      `[${failureTime}] ❌ Order router SignalR connection failed after ${totalAttemptTime}ms`,
    );
    console.error(`[${failureTime}] 🚨 Connection error details:`, {
      error: e.message || e,
      broker: rt.broker,
      serverUrl: rt.getUrl(),
      attemptDuration: totalAttemptTime,
    });

    // Log de erro para o usuário
    logger.orders.error("Erro na conexão com servidor de roteador de ordens", {
      error: e.message || e,
      server: rt.getUrl(),
      duration: totalAttemptTime,
    });

    // Attempt load balancing failover if multiple servers configured
    const urls = parsedBrokerUrls[marketdata.broker] || [];
    if (urls.length > 1) {
      const nextUrl = urlManager.markFailureAndGetNext(
        marketdata.broker,
        "broker",
        urls,
        e,
      );
      if (nextUrl) {
        console.log(
          `[${failureTime}] 🔄 Load balancer: Trying next broker server immediately: ${nextUrl}`,
        );
        // Retry connection immediately with new server (no delay, no attempt increment)
        timeoutManager.setTimeout(() => {
          rt.runConnection();
        }, 1000); // Small 1-second delay to avoid tight loop
        return; // Exit early - don't fall through to normal reconnection logic
      }
    }

    if (e.message) {
      if (e.message.toLowerCase().indexOf("forbidden") !== -1) {
        console.error(
          `[${failureTime}] 🔐 Authentication failed - invalid credentials`,
        );
        utilities.errorHandler(
          "Conexão negada! Sua autenticação é inválida ou está expirada",
        );
      } else {
        console.error(
          `[${failureTime}] 🌐 Network/Server error during connection`,
        );
        utilities.errorHandler(e);
      }
    } else {
      console.log(`[${failureTime}] ⏳ Unknown connection error`);
      utilities.errorHandler(e);
    }
  }
};

/**
 * Desfecho de zerar/cancelar, por `Code` (CancellationStateEnum do roteador).
 *
 * O roteador SEMPRE relatou o desfecho — a máquina de estados vive em
 * `CancellationFeatureManager` e chega aqui em `Info.Code` + `Info.Text` (texto já pronto,
 * inclusive o "Verifique no HOMEBROKER" quando é o caso). O cliente lia só `AllOrdersCancelled`
 * e usava os demais apenas como gatilho de refresh: `PositionTerminationFailed`, `Timeout` e
 * `CancelRejected` — os três avisos PERIGOSOS — passavam silenciosos.
 *
 * Era essa lacuna que a verificação por snapshot em `closePositionsForSymbol` tentava cobrir,
 * dormindo 4s e adivinhando pelo estado da conta — o que gera falso positivo quando o trader
 * abre posição nova na janela (OrdersRouter#8). Aqui o desfecho é o do roteador, não um palpite.
 *
 * `toast: null` = sem toast (evento de início; ruído puro).
 */
const CANCELLATION_OUTCOMES = {
  // `coalesce: true` = desfecho BOM; agrupa (ver COALESCE_MS). Um clique em "Zerar/Cancelar" gera
  // DOIS batches no roteador (cancelRouterOrdersForSymbol + closePositionsForSymbol) e o zerar
  // global dispara um POR SÍMBOLO — sem agrupar, o trader levaria uma pilha de toasts.
  // Falha NUNCA agrupa: cada uma é acionável e aponta um símbolo específico.
  PositionTerminated: {
    toast: "showInfo",
    title: "Posição zerada",
    log: "info",
    coalesce: true,
  },
  AllOrdersCancelled: {
    toast: "showSuccess",
    title: "Ordens canceladas",
    log: "info",
    coalesce: true,
  },
  PositionTerminationFailed: {
    toast: "showError",
    title: "Falha ao zerar",
    log: "error",
  },
  Timeout: {
    toast: "showWarning",
    title: "Verifique sua posição",
    log: "warning",
  },
  CancelRejected: {
    toast: "showError",
    title: "Cancelamento rejeitado",
    log: "error",
  },
  AlreadyInProgress: { toast: null, log: "info" },
  StartCancellingOrders: { toast: null, log: "info" },
  NoOpenOrdersToCancel: { toast: null, log: "info" },
  StartTerminatingPosition: { toast: null, log: "info" },
  NoPositionToTerminate: { toast: null, log: "info" },
};

/** Janela de agrupamento dos desfechos bons. Curta: o toast não pode parecer atrasado. */
const COALESCE_MS = 900;
let pendingOutcomes = [];
let coalesceTimer = null;

const flushPendingOutcomes = () => {
  coalesceTimer = null;
  const itens = pendingOutcomes;
  pendingOutcomes = [];
  if (itens.length === 0) return;

  if (itens.length === 1) {
    const { toast, title, message } = itens[0];
    globalToast[toast](message, title);
    return;
  }

  // Consolidado: uma linha por tipo de desfecho, símbolos sem repetir.
  const porTitulo = new Map();
  for (const { title, symbol } of itens) {
    if (!porTitulo.has(title)) porTitulo.set(title, new Set());
    if (symbol) porTitulo.get(title).add(symbol);
  }
  const resumo = [...porTitulo.entries()]
    .map(([titulo, simbolos]) =>
      simbolos.size > 0 ? `${titulo}: ${[...simbolos].join(", ")}` : titulo,
    )
    .join(" · ");

  // showInfo: consolidado é sempre desfecho bom (falha nunca entra na fila).
  globalToast.showInfo(resumo, "Zerar / Cancelar");
};

const queueOutcomeToast = (entry) => {
  pendingOutcomes.push(entry);
  if (coalesceTimer) clearTimeout(coalesceTimer);
  coalesceTimer = setTimeout(flushPendingOutcomes, COALESCE_MS);
};

export const receiveInfo = (rt, dataString) => {
  var info = JSON.parse(dataString);
  if (import.meta.env.DEV) {
    console.log("Info received:", info);
  }

  const message = info.Text ? decodeURIComponent(info.Text) : "";
  const outcome = CANCELLATION_OUTCOMES[info.Code];

  if (outcome) {
    // Todo desfecho vai para o painel de Log, com ou sem toast — o Log é a trilha que sobra
    // depois que o toast some.
    logger.orders[outcome.log](message || info.Code, {
      code: info.Code,
      symbol: info.Symbol,
      reference: info.Reference,
    });

    if (outcome.toast && message) {
      if (outcome.coalesce) {
        queueOutcomeToast({
          toast: outcome.toast,
          title: outcome.title,
          message,
          symbol: info.Symbol,
        });
      } else {
        globalToast[outcome.toast](message, outcome.title);
      }
    }
  } else if (message) {
    // Info fora da máquina de cancelamento (ex.: bloqueios do Daemon, avisos de versão).
    logger.orders.info(message, { code: info.Code, symbol: info.Symbol });
    if (info.IsWarning) {
      globalToast.showWarning(message, "Aviso");
    }
  }

  // Critical events - use immediate notification
  if (
    info.Code == "PositionTerminated" ||
    info.Code == "PositionTerminationFailed" ||
    info.Code == "AllOrdersCancelled"
  )
    rt.fireUpdateAccountData(rt.accountData, true);
};

export const receiveHeartbeat = (rt, time) => {
  rt.lastHeartbeat = time;
};

export const isConnectionOK = (rt) => {
  if (rt.signalRConnection) {
    if (rt.signalRConnection.connection) {
      if (rt.signalRConnection.state) {
        if (rt.signalRConnection.state == "Connected") {
          return true;
        }
      }
    }
  }
  return false;
};

export const inTradeState = (rt) => {
  if (marketdata.state == "online") {
    if (rt.state == "online") {
      return true;
    }
  }
  return false;
};
